import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthApiService } from '@core/services/auth/auth-api.service';
import { ToastService } from '../services/toast.service';
import { Observable } from 'rxjs';
import { matchPermissionPattern } from './permission-patterns.utils';

/** Explanation of this file...
 * Guard that protects routes requiring authentication.
 *
 * How it works:
 * 1. Checks if the user is authenticated via AuthApiService
 * 2. If authenticated → allows navigation (returns true)
 * 3. If NOT authenticated → redirects to login page
 *
 * Usage in routing:
 * ```typescript
 * {
 *   path: 'dashboard',
 *   component: DashboardComponent,
 *   canActivate: [authApiGuard]  // Note: function, not class
 * }
 * ```
 *
 * Why functional guard instead of class-based?
 * - Angular 15+ recommends functional guards
 * - Less boilerplate code
 * - Easier to test
 * - Can use inject() for dependency injection
 */

export const authApiGuard: CanActivateFn = ():
  | Observable<boolean | UrlTree>
  | boolean
  | UrlTree => {
  const authApiService = inject(AuthApiService);
  const router = inject(Router);

  if (authApiService.isAuthenticated()) {
    return true;
  }

  console.log('[authApiGuard] User not authenticated, redirecting to login');
  return router.createUrlTree(['/auth']);
};

/**
 * Guard that protects routes that should only be accessible when NOT logged in.
 *
 * Use case: Prevent logged-in users from accessing the login page.
 * If they're already logged in, redirect them to the dashboard.
 *
 * Usage in routing:
 * ```typescript
 * {
 *   path: 'auth/sign-in',
 *   component: SignInComponent,
 *   canActivate: [noAuthApiGuard]
 * }
 * ```
 */

export const noAuthApiGuard: CanActivateFn = ():
  | Observable<boolean | UrlTree>
  | boolean
  | UrlTree => {
  const authApiService = inject(AuthApiService);
  const router = inject(Router);

  // if user IS authenticated, redirect away from login page
  if (authApiService.isAuthenticated()) {
    console.log('[noAuthApiGuard] User already authenticated, redirecting to home');
    return router.createUrlTree(['/']);
  }

  // if NOT authenticated, allow access to login page
  return true;
};

export function roleApiGuard(allowedPermissions: string[]): CanActivateFn {
  return (): Observable<boolean | UrlTree> | boolean | UrlTree => {
    const authApiService = inject(AuthApiService);
    const router = inject(Router);
    const toastService = inject(ToastService);

    const token = authApiService.getToken();

    // No token means unauthenticated user.
    if (!token) {
      return router.createUrlTree(['/auth']);
    }

    // Even if token is close to/after expiration, avoid forced local logout here.
    // API requests rely on interceptor-based refresh + single retry flow.
    if (authApiService.isTokenExpired()) {
      console.warn('[roleApiGuard] Token appears expired; relying on interceptor refresh flow');
    }

    // Validate tenant consistency on native/mobile where client id is explicitly stored.
    const activeClientId = authApiService.getClientId();
    const tokenClientId = authApiService.getTokenClientId();
    if (activeClientId && tokenClientId && activeClientId !== tokenClientId) {
      console.warn('[roleApiGuard] Client ID mismatch between token and active context');
      return router.createUrlTree(['/auth']);
    }

    // Superadmin bypass from token claim.
    if (authApiService.getIsAdminFromToken()) {
      return true;
    }

    const tokenPermissions = authApiService.getPermissionsFromToken();
    const hasPermission = allowedPermissions.some((permission) =>
      matchPermissionPattern(permission, tokenPermissions),
    );
    if (hasPermission) {
      return true;
    }

    // User doesn't have required permission - show feedback and keep current page
    console.warn(
      '[roleApiGuard] User lacks required permission. Required:',
      allowedPermissions,
      'Has:',
      tokenPermissions,
    );
    toastService.showToast('GENERAL.AREA_NOT_ALLOWED', 'bg-danger', 'close');
    return false;
  };
}
